import React, { useState, useEffect } from 'react';
import ExositeCard from './ExositeCard';
import ProteinViewer from './ProteinViewer';
import NotFoundSuggestions from './NotFoundSuggestions';
import ExportModal from './ExportModal';
import { ExositeEntry } from '../types';

interface BrowseFilters {
  proteinFamily: string;
  subfamily: string;
  organism: string;
  confidenceLevel: string;
  hasStructure: boolean;
  sortBy: 'name' | 'confidence' | 'publications';
  sortOrder: 'asc' | 'desc';
}

interface BrowseProps {
  entries: ExositeEntry[];
}

const Browse: React.FC<BrowseProps> = ({ entries }) => {
  const [filteredEntries, setFilteredEntries] = useState<ExositeEntry[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<BrowseFilters>({
    proteinFamily: '',
    subfamily: '',
    organism: '',
    confidenceLevel: '',
    hasStructure: false,
    sortBy: 'confidence',
    sortOrder: 'desc'
  });
  
  // 3D Viewer state
  const [selectedEntry, setSelectedEntry] = useState<ExositeEntry | null>(null);
  const [showViewer, setShowViewer] = useState(false);
  const [showFullscreenViewer, setShowFullscreenViewer] = useState(false);
  const [viewerForEntry, setViewerForEntry] = useState<string | null>(null); // Track which entry has viewer open
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [showFilters, setShowFilters] = useState(false); // Mobile filter toggle
  
  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isSmallMobile = windowWidth < 480;

  // Helper function to handle PDB IDs (array or comma-separated string)
  const getPdbIdArray = (pdbIds: any): string[] => {
    if (!pdbIds) return [];
    if (Array.isArray(pdbIds)) return pdbIds;
    if (typeof pdbIds === 'string') return pdbIds.split(',').map(id => id.trim());
    return [];
  };

  // Generate unique ID for entry
  const getEntryId = (entry: ExositeEntry, index: number): string => {
    return entry.entry_type === 'individual' 
      ? `individual-${entry.individual_entry?.id || index}`
      : `grouped-${entry.grouped_entry?.protein_name || index}`.replace(/\s+/g, '-');
  };

  // Handle 3D viewer for specific entry
  const handleViewStructure = (pdbId: string, targetEntry?: ExositeEntry) => {
    
    // Find the entry that contains this PDB ID if not provided
    let entry = targetEntry;
    if (!entry) {
      entry = filteredEntries.find(e => {
        if (e.entry_type === 'grouped') {
          const pdbIdArray = getPdbIdArray(e.grouped_entry?.representative_pdb_ids);
          return pdbIdArray.includes(pdbId);
        } else if (e.entry_type === 'individual') {
          const pdbIdArray = getPdbIdArray(e.individual_entry?.pdb_id);
          return pdbIdArray.includes(pdbId);
        }
        return false;
      });
    }
    
    if (entry) {
      const entryId = getEntryId(entry, 0);
      
      if (viewerForEntry === entryId && showViewer) {
        // Close viewer if it's already open for this entry
        setShowViewer(false);
        setViewerForEntry(null);
        setSelectedEntry(null);
      } else {
        // Open viewer for this entry
        setSelectedEntry(entry);
        setShowViewer(true);
        setViewerForEntry(entryId);
      }
    }
  };

  const ENTRIES_PER_PAGE = isMobile ? 8 : 12;

  // Helper function to create a readable query string from applied filters
  const getAppliedFiltersQuery = (): string => {
    const activeFilters = [];
    
    if (filters.proteinFamily) activeFilters.push(filters.proteinFamily);
    if (filters.organism) activeFilters.push(filters.organism);
    if (filters.confidenceLevel) {
      const confidenceLabels: Record<string, string> = {
        'HIGH': 'High only',
        'MEDIUM': 'Medium only',
        'LOW': 'Low only'
      };
      activeFilters.push(confidenceLabels[filters.confidenceLevel] || 'Confidence Filter');
    }
    if (filters.hasStructure) activeFilters.push('Has Structure');
    
    return activeFilters.length > 0 ? activeFilters.join(' + ') : 'Filtered results';
  };

  // Helper function to determine search type from filters
  const getSearchTypeFromFilters = (): string => {
    if (filters.proteinFamily) return 'protein_family';
    if (filters.organism) return 'organism'; 
    return 'browse_filter';
  };

  // Reset dependent filters when protein family changes
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      subfamily: '', // Reset subfamily when protein family changes
      organism: '', // Reset organism when protein family changes
      // Keep other filters as they don't depend on protein family
    }));
  }, [filters.proteinFamily]);

  // Apply filters and sorting
  useEffect(() => {
    let filtered = [...entries];

    // Apply filters
    if (filters.proteinFamily) {
      filtered = filtered.filter(entry => {
        const familyClass = entry.entry_type === 'grouped'
          ? entry.grouped_entry?.best_entry?.family_class || ''
          : entry.individual_entry?.family_class || '';

        return familyClass.toLowerCase() === filters.proteinFamily.toLowerCase();
      });
    }

    if (filters.subfamily) {
      filtered = filtered.filter(entry => {
        const subfamily = entry.entry_type === 'grouped'
          ? entry.grouped_entry?.best_entry?.subfamily || ''
          : entry.individual_entry?.subfamily || '';

        return subfamily.toLowerCase() === filters.subfamily.toLowerCase();
      });
    }

    if (filters.organism) {
      filtered = filtered.filter(entry => {
        const organism = entry.entry_type === 'grouped'
          ? entry.grouped_entry?.best_entry?.organism?.toLowerCase() || ''
          : entry.individual_entry?.organism?.toLowerCase() || '';
        return organism.includes(filters.organism.toLowerCase());
      });
    }

    if (filters.confidenceLevel) {
      const selectedLevel = filters.confidenceLevel; // 'HIGH' | 'MEDIUM' | 'LOW'
      filtered = filtered.filter(entry => {
        if (entry.entry_type === 'grouped') {
          // Use aggregated evidence quality; fallback to simple derivation
          let groupLevel = (entry.grouped_entry?.evidence_quality || '').toUpperCase();
          if (!groupLevel) {
            const hc = entry.grouped_entry?.high_confidence_count || 0;
            const vc = entry.grouped_entry?.validated_count || 0;
            groupLevel = hc > 0 ? 'HIGH' : (vc > 0 ? 'MEDIUM' : 'LOW');
          }
          return groupLevel === selectedLevel.toUpperCase();
        } else {
          const confidenceLevel = (entry.individual_entry?.confidence_level || '').toUpperCase();
          if (!confidenceLevel) return false;
          return confidenceLevel === selectedLevel.toUpperCase();
        }
      });
    }

    if (filters.hasStructure) {
      filtered = filtered.filter(entry => {
        if (entry.entry_type === 'grouped') {
          return getPdbIdArray(entry.grouped_entry?.representative_pdb_ids).length > 0;
        } else {
          return entry.individual_entry?.pdb_id;
        }
      });
    }

    // Apply sorting: ALWAYS prioritize confidence first, then secondary sort
    filtered.sort((a, b) => {
      // PRIMARY SORT: Always by confidence (HIGH -> MEDIUM -> LOW)
      const aConfidence = a.entry_type === 'grouped'
        ? (a.grouped_entry?.high_confidence_count || 0) / (a.grouped_entry?.entry_count || 1)
        : (a.individual_entry?.confidence_level === 'HIGH' ? 1 :
           a.individual_entry?.confidence_level === 'MEDIUM' ? 0.5 : 0.2);
      const bConfidence = b.entry_type === 'grouped'
        ? (b.grouped_entry?.high_confidence_count || 0) / (b.grouped_entry?.entry_count || 1)
        : (b.individual_entry?.confidence_level === 'HIGH' ? 1 :
           b.individual_entry?.confidence_level === 'MEDIUM' ? 0.5 : 0.2);

      // If confidence levels are different, sort by confidence (HIGH first)
      if (Math.abs(aConfidence - bConfidence) > 0.1) {
        return bConfidence - aConfidence; // Descending (HIGH first)
      }

      // SECONDARY SORT: If same confidence level, sort by selected criteria
      let aValue: any, bValue: any;

      switch (filters.sortBy) {
        case 'name':
          aValue = (a.entry_type === 'grouped'
            ? a.grouped_entry?.protein_name
            : a.individual_entry?.protein_name) || '';
          bValue = (b.entry_type === 'grouped'
            ? b.grouped_entry?.protein_name
            : b.individual_entry?.protein_name) || '';
          break;
        case 'confidence':
          // When sorting by confidence, use detailed score as secondary sort
          aValue = a.entry_type === 'individual'
            ? (a.individual_entry?.confidence_score_detailed || 0)
            : aConfidence;
          bValue = b.entry_type === 'individual'
            ? (b.individual_entry?.confidence_score_detailed || 0)
            : bConfidence;
          break;
        case 'publications':
          aValue = a.entry_type === 'grouped'
            ? a.grouped_entry?.entry_count || 0
            : 1;
          bValue = b.entry_type === 'grouped'
            ? b.grouped_entry?.entry_count || 0
            : 1;
          break;
        default:
          aValue = bValue = 0;
      }

      // Apply secondary sort
      if (typeof aValue === 'string') {
        return filters.sortOrder === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        return filters.sortOrder === 'asc'
          ? aValue - bValue
          : bValue - aValue;
      }
    });

    setFilteredEntries(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [entries, filters]);

  // Helper function to extract family name consistently
  const extractFamilyName = (proteinName: string): string => {
    const cleanName = proteinName.trim().toLowerCase();
    
    // Remove content in parentheses first
    const withoutParens = cleanName.replace(/\s*\([^)]*\)/g, '').trim();
    
    // Define priority patterns - check specific proteins first
    const patterns = [
      // Thrombin family patterns
      { regex: /\b(anti)?thrombin\b/, family: 'Thrombin' },
      { regex: /\balpha[\s-]*thrombin\b/, family: 'Thrombin' },
      { regex: /\bprothrombin\b/, family: 'Thrombin' },
      
      // Factor patterns
      { regex: /\bfactor\s*([ivx\d]+[a-z]?)\b/i, family: 'Factor' },
      { regex: /\bcoagulation\s*factor\b/, family: 'Factor' },
      
      // ADAMTS patterns
      { regex: /\badamts[-]?(\d+)?\b/, family: 'Adamts' },
      
      // Kinase patterns
      { regex: /\b\w*kinase\b/, family: 'Kinase' },
      { regex: /\bmap\s*kinase\b/, family: 'Kinase' },
      { regex: /\bprotein\s*kinase\b/, family: 'Kinase' },
      
      // Matrix Metalloproteinase patterns
      { regex: /\bmmp[-]?\d+\b/, family: 'Mmp' },
      { regex: /\bmatrix\s*metalloproteinase\b/, family: 'Mmp' },
      { regex: /\bmetalloproteinase\b/, family: 'Metalloproteinase' },
      
      // Membrane proteins
      { regex: /\btransmembrane\b/, family: 'Transmembrane' },
      { regex: /\bmembrane\s*protein\b/, family: 'Transmembrane' },
      { regex: /\breceptor\b/, family: 'Receptor' },
      { regex: /\bchannel\b/, family: 'Channel' },
      { regex: /\btransporter\b/, family: 'Transporter' },
      
      // Other specific patterns
      { regex: /\bcaspase\b/, family: 'Caspase' },
      { regex: /\bcathepsin\b/, family: 'Cathepsin' },
      { regex: /\bphosphatase\b/, family: 'Phosphatase' },
      { regex: /\bhemoglobin\b/, family: 'Hemoglobin' },
      { regex: /\bcyclin\b/, family: 'Cyclin' }
    ];
    
    // Check each pattern
    for (const pattern of patterns) {
      if (pattern.regex.test(withoutParens)) {
        return pattern.family;
      }
    }
    
    // Fallback: Take first meaningful word
    const words = withoutParens.split(/[\s\-_,]+/);
    const skipWords = ['human', 'recombinant', 'wild', 'type', 'protein', 'enzyme', 'bovine', 'mouse', 'rat'];
    const meaningfulWords = words.filter(word => 
      word.length > 2 && !skipWords.includes(word) && word !== ''
    );
    
    if (meaningfulWords.length > 0) {
      const familyName = meaningfulWords[0];
      return familyName.charAt(0).toUpperCase() + familyName.slice(1);
    }
    
    return '';
  };

  // Get filtered entries based on current protein family selection
  const getFilteredEntriesForDropdowns = () => {
    if (!filters.proteinFamily) return entries;

    return entries.filter(entry => {
      const familyClass = entry.entry_type === 'grouped'
        ? entry.grouped_entry?.best_entry?.family_class || ''
        : entry.individual_entry?.family_class || '';

      return familyClass.toLowerCase() === filters.proteinFamily.toLowerCase();
    });
  };

  // Get unique values for filter dropdowns based on current selections
  const getUniqueOrganisms = () => {
    const organisms = new Set<string>();
    const relevantEntries = getFilteredEntriesForDropdowns();
    
    relevantEntries.forEach(entry => {
      const organism = entry.entry_type === 'grouped'
        ? entry.grouped_entry?.best_entry?.organism
        : entry.individual_entry?.organism;
      if (organism) organisms.add(organism);
    });
    return Array.from(organisms).sort();
  };

  const getProteinFamilies = () => {
    const families = new Set<string>();

    entries.forEach((entry) => {
      const familyClass = entry.entry_type === 'grouped'
        ? entry.grouped_entry?.best_entry?.family_class
        : entry.individual_entry?.family_class;

      if (familyClass) {
        families.add(familyClass);
      }
    });

    const familyList = Array.from(families).sort();
    return familyList;
  };

  const getUniqueSubfamilies = () => {
    if (!filters.proteinFamily) return [];

    const subfamilies = new Set<string>();
    const relevantEntries = entries.filter(entry => {
      const familyClass = entry.entry_type === 'grouped'
        ? entry.grouped_entry?.best_entry?.family_class || ''
        : entry.individual_entry?.family_class || '';
      return familyClass.toLowerCase() === filters.proteinFamily.toLowerCase();
    });

    relevantEntries.forEach(entry => {
      const subfamily = entry.entry_type === 'grouped'
        ? entry.grouped_entry?.best_entry?.subfamily
        : entry.individual_entry?.subfamily;
      if (subfamily) {
        subfamilies.add(subfamily);
      }
    });

    return Array.from(subfamilies).sort();
  };

  // Pagination
  const totalPages = Math.ceil(filteredEntries.length / ENTRIES_PER_PAGE);
  const startIndex = (currentPage - 1) * ENTRIES_PER_PAGE;
  const paginatedEntries = filteredEntries.slice(startIndex, startIndex + ENTRIES_PER_PAGE);


  return (
    <div style={{ 
      display: 'flex', 
      flexDirection: isMobile ? 'column' : 'row',
      gap: isMobile ? '16px' : '32px' 
    }}>
      {/* Filter Sidebar - Mobile Responsive */}
      <div style={{ 
        width: isMobile ? '100%' : '280px', 
        flexShrink: 0,
        order: isMobile ? 1 : 1
      }}>
        {/* Mobile filter toggle button */}
        {isMobile && (
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              width: '100%',
              padding: '12px 16px',
              marginBottom: '16px',
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <span>🔧 Filters & Sorting</span>
            <span>{showFilters ? '▲' : '▼'}</span>
          </button>
        )}

        <div className="card" style={{ 
          position: isMobile ? 'static' : 'sticky', 
          top: '20px',
          display: isMobile && !showFilters ? 'none' : 'block'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px'
          }}>
            <h3 style={{ 
              fontSize: isMobile ? '16px' : '18px', 
              fontWeight: '600', 
              color: '#1e293b',
              margin: 0
            }}>
              Filters & Sorting
            </h3>
            {isMobile && (
              <button
                onClick={() => setShowFilters(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '16px',
                  cursor: 'pointer',
                  color: '#64748b'
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Protein Family Filter */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontSize: isMobile ? '13px' : '14px',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '6px'
            }}>
              Protein Family
            </label>
            <select
              value={filters.proteinFamily}
              onChange={(e) => setFilters(prev => ({ ...prev, proteinFamily: e.target.value }))}
              style={{
                width: '100%',
                padding: isMobile ? '6px 10px' : '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: isMobile ? '13px' : '14px'
              }}
            >
              <option value="">All Families</option>
              {getProteinFamilies().map(family => (
                <option key={family} value={family}>{family}</option>
              ))}
            </select>
          </div>

          {/* Sub-Family Filter - Only shown when a family is selected and has subfamilies */}
          {filters.proteinFamily && getUniqueSubfamilies().length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: isMobile ? '13px' : '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Sub-Family
                <span style={{
                  fontSize: isMobile ? '11px' : '12px',
                  color: '#6b7280',
                  fontWeight: '400',
                  marginLeft: '4px'
                }}>
                  (within {filters.proteinFamily})
                </span>
              </label>
              <select
                value={filters.subfamily}
                onChange={(e) => setFilters(prev => ({ ...prev, subfamily: e.target.value }))}
                style={{
                  width: '100%',
                  padding: isMobile ? '6px 10px' : '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: isMobile ? '13px' : '14px',
                  backgroundColor: '#f9fafb'
                }}
              >
                <option value="">All Sub-Families in {filters.proteinFamily}</option>
                {getUniqueSubfamilies().map(subfamily => (
                  <option key={subfamily} value={subfamily}>{subfamily}</option>
                ))}
              </select>
              <div style={{
                fontSize: isMobile ? '11px' : '12px',
                color: '#6b7280',
                marginTop: '4px'
              }}>
                {getUniqueSubfamilies().length} sub-family(ies) available
              </div>
            </div>
          )}

          {/* Organism Filter */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: isMobile ? '13px' : '14px', 
              fontWeight: '500', 
              color: '#374151', 
              marginBottom: '6px' 
            }}>
              Organism
              {filters.proteinFamily && (
                <span style={{ 
                  fontSize: isMobile ? '11px' : '12px', 
                  color: '#6b7280', 
                  fontWeight: '400',
                  marginLeft: '4px'
                }}>
                  (filtered by {filters.proteinFamily})
                </span>
              )}
            </label>
            <select
              value={filters.organism}
              onChange={(e) => setFilters(prev => ({ ...prev, organism: e.target.value }))}
              style={{
                width: '100%',
                padding: isMobile ? '6px 10px' : '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: isMobile ? '13px' : '14px',
                backgroundColor: filters.proteinFamily ? '#f9fafb' : 'white'
              }}
            >
              <option value="">
                {filters.proteinFamily ? `All Organisms in ${filters.proteinFamily}` : 'All Organisms'}
              </option>
              {getUniqueOrganisms().map(organism => (
                <option key={organism} value={organism}>{organism}</option>
              ))}
            </select>
            {filters.proteinFamily && getUniqueOrganisms().length > 0 && (
              <div style={{ 
                fontSize: isMobile ? '11px' : '12px', 
                color: '#6b7280', 
                marginTop: '4px' 
              }}>
                {getUniqueOrganisms().length} organism(s) available in {filters.proteinFamily}
              </div>
            )}
          </div>

          {/* Confidence Level Filter (Exact selection) */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: isMobile ? '13px' : '14px', 
              fontWeight: '500', 
              color: '#374151', 
              marginBottom: '6px' 
            }}>
              Confidence Level
            </label>
            <select
              value={filters.confidenceLevel}
              onChange={(e) => setFilters(prev => ({ ...prev, confidenceLevel: e.target.value }))}
              style={{
                width: '100%',
                padding: isMobile ? '6px 10px' : '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: isMobile ? '13px' : '14px'
              }}
            >
              <option value="">All Confidence Levels</option>
              <option value="HIGH">High only</option>
              <option value="MEDIUM">Medium only</option>
              <option value="LOW">Low only</option>
            </select>
          </div>

          {/* Structure Filter */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={filters.hasStructure}
                onChange={(e) => setFilters(prev => ({ ...prev, hasStructure: e.target.checked }))}
                style={{ width: '16px', height: '16px' }}
              />
              <span style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '500', color: '#374151' }}>
                Has 3D Structure
              </span>
            </label>
          </div>

          {/* Sort Options - Commented out as not particularly useful for browsing */}
          {/*
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              fontSize: isMobile ? '13px' : '14px', 
              fontWeight: '500', 
              color: '#374151', 
              marginBottom: '6px' 
            }}>
              Sort By
            </label>
            <select
              value={filters.sortBy}
              onChange={(e) => setFilters(prev => ({ 
                ...prev, 
                sortBy: e.target.value as 'name' | 'confidence' | 'publications' 
              }))}
              style={{
                width: '100%',
                padding: isMobile ? '6px 10px' : '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: isMobile ? '13px' : '14px',
                marginBottom: '8px'
              }}
            >
              <option value="name">Protein Name</option>
              <option value="confidence">Confidence Score</option>
              <option value="publications">Publication Count</option>
            </select>

            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                onClick={() => setFilters(prev => ({ ...prev, sortOrder: 'asc' }))}
                style={{
                  flex: 1,
                  padding: isMobile ? '4px 8px' : '6px 12px',
                  border: `1px solid ${filters.sortOrder === 'asc' ? '#22c55e' : '#d1d5db'}`,
                  backgroundColor: filters.sortOrder === 'asc' ? '#f0fdf4' : 'white',
                  color: filters.sortOrder === 'asc' ? '#22c55e' : '#374151',
                  borderRadius: '4px',
                  fontSize: isMobile ? '11px' : '12px',
                  cursor: 'pointer'
                }}
              >
                Ascending
              </button>
              <button
                onClick={() => setFilters(prev => ({ ...prev, sortOrder: 'desc' }))}
                style={{
                  flex: 1,
                  padding: isMobile ? '4px 8px' : '6px 12px',
                  border: `1px solid ${filters.sortOrder === 'desc' ? '#22c55e' : '#d1d5db'}`,
                  backgroundColor: filters.sortOrder === 'desc' ? '#f0fdf4' : 'white',
                  color: filters.sortOrder === 'desc' ? '#22c55e' : '#374151',
                  borderRadius: '4px',
                  fontSize: isMobile ? '11px' : '12px',
                  cursor: 'pointer'
                }}
              >
                Descending
              </button>
            </div>
          </div>
          */}

          {/* Results Summary */}
          <div style={{ 
            padding: isMobile ? '8px' : '12px', 
            backgroundColor: '#f8fafc', 
            borderRadius: '6px',
            fontSize: isMobile ? '12px' : '14px',
            color: '#64748b'
          }}>
            Showing {paginatedEntries.length} of {filteredEntries.length} proteins
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ 
        flex: 1,
        order: isMobile ? 2 : 2
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center', 
          justifyContent: 'space-between',
          marginBottom: isMobile ? '16px' : '24px',
          gap: isMobile ? '12px' : '0'
        }}>
          <h2 style={{ 
            fontSize: isMobile ? '20px' : '28px', 
            fontWeight: 'bold', 
            color: '#1e293b',
            margin: 0
          }}>
            Browse Database
          </h2>
          
          {/* Export Button */}
          {filteredEntries.length > 0 && (
            <button
              onClick={() => {
                setShowExportModal(true);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: isMobile ? '10px 16px' : '12px 20px',
                backgroundColor: 'white',
                color: '#374151',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                fontSize: isMobile ? '13px' : '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                position: 'relative',
                letterSpacing: '-0.005em'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f9fafb';
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.color = '#1f2937';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.color = '#374151';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
              }}
              title="Export filtered browse results to CSV"
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '18px',
                height: '18px',
                backgroundColor: '#f3f4f6',
                borderRadius: '5px',
                flexShrink: 0
              }}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7,10 12,15 17,10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
              </div>
              <span style={{ fontSize: isMobile ? '13px' : '14px', fontWeight: '600' }}>
                Export Filtered Results
              </span>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '20px',
                height: '18px',
                backgroundColor: '#f3f4f6',
                borderRadius: '9px',
                fontSize: '10px',
                fontWeight: '700',
                paddingLeft: '6px',
                paddingRight: '6px',
                color: '#6b7280'
              }}>
                {filteredEntries.length}
              </div>
            </button>
          )}
        </div>

        {/* Results */}
        {paginatedEntries.length > 0 ? (
          <>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: isMobile ? '16px' : '24px', 
              marginBottom: isMobile ? '24px' : '32px' 
            }}>
              {paginatedEntries.map((entry, index) => {
                const entryId = getEntryId(entry, index);
                const hasViewer = viewerForEntry === entryId && showViewer && selectedEntry;
                
                return (
                  <div key={entryId} style={{ width: '100%' }}>
                    {/* Entry Card */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: isMobile && hasViewer ? 'column' : 'row',
                      gap: hasViewer && !isMobile ? '24px' : '0',
                      alignItems: hasViewer && !isMobile ? 'flex-start' : 'stretch'
                    }}>
                      <div style={{ 
                        width: hasViewer && !isMobile ? 'calc(100% - 504px)' : '100%',  // 504px = 480px viewer + 24px gap
                        transition: 'width 0.3s ease'
                      }}>
                        <ExositeCard
                          entry={entry}
                          onViewStructure={(pdbId) => handleViewStructure(pdbId, entry)}
                        />
                      </div>
                      
                      {/* 3D Viewer - Mobile Responsive */}
                      {hasViewer && (
                        <div style={{ 
                          width: isMobile ? '100%' : '480px', 
                          flexShrink: 0,
                          opacity: hasViewer ? 1 : 0,
                          transition: 'opacity 0.3s ease',
                          marginTop: isMobile ? '16px' : '0'
                        }}>
                          <ProteinViewer
                            pdbId={
                              selectedEntry.entry_type === 'grouped' 
                                ? getPdbIdArray(selectedEntry.grouped_entry?.representative_pdb_ids)[0] || ''
                                : getPdbIdArray(selectedEntry.individual_entry?.pdb_id)[0] || selectedEntry.individual_entry?.pdb_id || ''
                            }
                            validatedResidues={
                              selectedEntry.entry_type === 'grouped'
                                ? selectedEntry.grouped_entry?.best_entry?.validated_pdb_residues
                                : selectedEntry.individual_entry?.validated_pdb_residues
                            }
                            mappingStatus={
                              selectedEntry.entry_type === 'grouped'
                                ? selectedEntry.grouped_entry?.best_entry?.pdb_mapping_status
                                : selectedEntry.individual_entry?.pdb_mapping_status
                            }
                            mappingValidationReason={
                              selectedEntry.entry_type === 'grouped'
                                ? selectedEntry.grouped_entry?.best_entry?.pdb_mapping_validation_reason
                                : selectedEntry.individual_entry?.pdb_mapping_validation_reason
                            }
                            availablePdbIds={
                              selectedEntry.entry_type === 'grouped'
                                ? getPdbIdArray(selectedEntry.grouped_entry?.representative_pdb_ids)
                                : getPdbIdArray(selectedEntry.individual_entry?.pdb_id)
                            }
                            representativePdbId={
                              selectedEntry.entry_type === 'grouped' 
                                ? getPdbIdArray(selectedEntry.grouped_entry?.representative_pdb_ids)[0] || ''
                                : getPdbIdArray(selectedEntry.individual_entry?.pdb_id)[0] || selectedEntry.individual_entry?.pdb_id || ''
                            }
                            onClose={() => {
                              setShowViewer(false);
                              setViewerForEntry(null);
                              setSelectedEntry(null);
                            }}
                            onExpand={() => {
                              setShowFullscreenViewer(true);
                            }}
                            bindingPartners={
                              selectedEntry.entry_type === 'grouped'
                                ? selectedEntry.grouped_entry?.best_entry?.binding_partner || 
                                  selectedEntry.grouped_entry?.best_entry?.binding_partners
                                : selectedEntry.individual_entry?.binding_partner || 
                                  selectedEntry.individual_entry?.binding_partners
                            }
                            entryId={
                              selectedEntry.entry_type === 'grouped'
                                ? selectedEntry.grouped_entry?.best_entry?.id
                                : selectedEntry.individual_entry?.id
                            }
                            onPdbChange={(newPdbId) => {
                              // Handle PDB change
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination - Mobile Responsive */}
            {totalPages > 1 && (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: isMobile ? '4px' : '8px',
                marginTop: isMobile ? '24px' : '32px',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: isMobile ? '6px 8px' : '8px 12px',
                    border: '1px solid #d1d5db',
                    backgroundColor: currentPage === 1 ? '#f9fafb' : 'white',
                    color: currentPage === 1 ? '#9ca3af' : '#374151',
                    borderRadius: '6px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: isMobile ? '12px' : '14px'
                  }}
                >
                  {isMobile ? 'Prev' : 'Previous'}
                </button>
                
                <span style={{ 
                  padding: isMobile ? '6px 8px' : '8px 16px',
                  fontSize: isMobile ? '12px' : '14px',
                  color: '#64748b'
                }}>
                  Page {currentPage} of {totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: isMobile ? '6px 8px' : '8px 12px',
                    border: '1px solid #d1d5db',
                    backgroundColor: currentPage === totalPages ? '#f9fafb' : 'white',
                    color: currentPage === totalPages ? '#9ca3af' : '#374151',
                    borderRadius: '6px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontSize: isMobile ? '12px' : '14px'
                  }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <NotFoundSuggestions
            query={getAppliedFiltersQuery()}
            searchType={getSearchTypeFromFilters()}
            onRetry={() => {
              // Reset all filters
              setFilters({
                proteinFamily: '',
                subfamily: '',
                organism: '',
                confidenceLevel: '',
                hasStructure: false,
                sortBy: 'confidence',
                sortOrder: 'desc'
              });
            }}
            onBrowse={() => {
              // Already on browse, just reset filters
              setFilters({
                proteinFamily: '',
                subfamily: '',
                organism: '',
                confidenceLevel: '',
                hasStructure: false,
                sortBy: 'confidence',
                sortOrder: 'desc'
              });
            }}
          />
        )}

        {/* Fullscreen 3D Viewer Modal (same as Search page) */}
        {showFullscreenViewer && selectedEntry && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }} onClick={() => setShowFullscreenViewer(false)}>
            <div style={{
              width: '90vw',
              height: '90vh',
              backgroundColor: 'white',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
            }} onClick={(e) => e.stopPropagation()}>
              <ProteinViewer
                pdbId={
                  selectedEntry.entry_type === 'grouped' 
                    ? getPdbIdArray(selectedEntry.grouped_entry?.representative_pdb_ids)[0] || ''
                    : getPdbIdArray(selectedEntry.individual_entry?.pdb_id)[0] || selectedEntry.individual_entry?.pdb_id || ''
                }
                validatedResidues={
                  selectedEntry.entry_type === 'grouped'
                    ? selectedEntry.grouped_entry?.best_entry?.validated_pdb_residues
                    : selectedEntry.individual_entry?.validated_pdb_residues
                }
                mappingStatus={
                  selectedEntry.entry_type === 'grouped'
                    ? selectedEntry.grouped_entry?.best_entry?.pdb_mapping_status
                    : selectedEntry.individual_entry?.pdb_mapping_status
                }
                mappingValidationReason={
                  selectedEntry.entry_type === 'grouped'
                    ? selectedEntry.grouped_entry?.best_entry?.pdb_mapping_validation_reason
                    : selectedEntry.individual_entry?.pdb_mapping_validation_reason
                }
                availablePdbIds={
                  selectedEntry.entry_type === 'grouped'
                    ? getPdbIdArray(selectedEntry.grouped_entry?.representative_pdb_ids)
                    : getPdbIdArray(selectedEntry.individual_entry?.pdb_id)
                }
                representativePdbId={
                  selectedEntry.entry_type === 'grouped' 
                    ? getPdbIdArray(selectedEntry.grouped_entry?.representative_pdb_ids)[0] || ''
                    : getPdbIdArray(selectedEntry.individual_entry?.pdb_id)[0] || selectedEntry.individual_entry?.pdb_id || ''
                }
                onClose={() => {
                  setShowFullscreenViewer(false);
                }}
                isFullscreen={true}
                bindingPartners={
                  selectedEntry.entry_type === 'grouped'
                    ? selectedEntry.grouped_entry?.best_entry?.binding_partner || 
                      selectedEntry.grouped_entry?.best_entry?.binding_partners
                    : selectedEntry.individual_entry?.binding_partner || 
                      selectedEntry.individual_entry?.binding_partners
                }
                entryId={
                  selectedEntry.entry_type === 'grouped'
                    ? selectedEntry.grouped_entry?.best_entry?.id
                    : selectedEntry.individual_entry?.id
                }
                onPdbChange={(newPdbId) => {
                  // Handle PDB change in fullscreen
                }}
              />
            </div>
          </div>
        )}
        
        {/* Export Modal */}
        <ExportModal 
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          currentFilters={filters}
          specificResults={filteredEntries}
        />
      </div>
    </div>
  );
};

export default Browse;
